import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Filter, TrendingUp, MapPin, DollarSign, Award, MessageSquare, Heart, Send, CheckSquare, Square, Calendar, Building2, ArrowUpDown, Users, Globe, Lock, ChevronDown, ChevronUp, Sparkles, Brain, Lightbulb, AlertTriangle, CheckCircle, Zap, Shield, Briefcase, Star } from 'lucide-react';
import { toast } from 'sonner';
import FavoriteQualificationModal from '../components/FavoriteQualificationModal';
import PreReservationModal from '../components/PreReservationModal';
import ViewResourceProfileButton from '../components/ViewResourceProfileButton';
import BulkContactBar from '../components/BulkContactBar';
import BulkApplyBar from '../components/BulkApplyBar';
import { navigateWithState } from '../utils/navigationState';
import { SERVICENOW_PRODUCTS } from '../utils/servicenowData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdvancedMatchingPage = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const projectIdFromUrl = searchParams.get('project');
  const resourceIdFromUrl = searchParams.get('resource');
  
  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';
  const isPM = user?.role === 'project_manager';
  
  const [loading, setLoading] = useState(false);
  
  // PM mode: projects and selected project
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectIdFromUrl || '');
  const [project, setProject] = useState(null);
  
  // RM mode: resources and selected resource
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(resourceIdFromUrl || '');
  const [resource, setResource] = useState(null);
  
  // Common
  const [matches, setMatches] = useState([]);
  const [showBreakdown, setShowBreakdown] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  
  // Modals
  const [showFavoriteModal, setShowFavoriteModal] = useState(null);
  const [showPreReserveModal, setShowPreReserveModal] = useState(null);
  
  // Bulk selection
  const [selectedResources, setSelectedResources] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  
  // AI Matching
  const [enableAI, setEnableAI] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    min_score: 0,
    certifications: '',
    seniority: '',
    min_rate: '',
    max_rate: '',
    location: '',
    availability: ''
  });
  
  // Advanced filters
  const [advancedFilters, setAdvancedFilters] = useState({
    companyTypes: [], // Array of selected company types
    sortBy: 'score', // 'score', 'price', 'availability', 'date'
    sortOrder: 'desc', // 'asc' or 'desc'
    internalMode: 'all', // 'all', 'internal', 'partners'
    aiAugmentedOnly: false, // Filter for AI-Augmented consultants only (PM view)
    prefersAiAugmented: false // NEW: Filter for projects preferring AI-Augmented (RM view)
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [favoritePartners, setFavoritePartners] = useState([]); // For internal mode
  const [userDomain, setUserDomain] = useState(''); // For internal mode

  // Company type options
  const COMPANY_TYPES = [
    { value: 'partner', label: 'ServiceNow Partner' },
    { value: 'consulting', label: 'Consulting Firm' },
    { value: 'recruitment', label: 'Recruitment Agency' },
    { value: 'servicenow_editor', label: 'ServiceNow Editor/ISV' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'end_client', label: 'End Client' }
  ];

  useEffect(() => {
    if (isPM) {
      fetchMyProjects();
    } else if (isRM) {
      fetchMyResources();
    }
    fetchFavorites();
    fetchUserDomainAndPartners();
  }, [isPM, isRM]);

  // Fetch user domain and favorite partners for internal mode
  const fetchUserDomainAndPartners = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      // Get user's email domain
      if (user?.email) {
        const domain = user.email.split('@')[1];
        setUserDomain(domain);
      }
      
      // Fetch favorite partners (companies marked as partners)
      const response = await axios.get(`${API}/favorites/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      setFavoritePartners(response.data || []);
    } catch (error) {
      console.log('Could not fetch partners:', error.message);
    }
  };

  // Restore state when returning from navigation
  useEffect(() => {
    // Check for restoreState OR direct selectedResourceId from resource card click
    if (location.state?.restoreState || location.state?.selectedResourceId) {
      console.log('🔄 Restoring matching state:', location.state);
      
      if (isPM && location.state.selectedProject) {
        setSelectedProject(location.state.selectedProject);
      } else if (isRM && location.state.selectedResource) {
        setSelectedResource(location.state.selectedResource);
      } else if (isRM && location.state.selectedResourceId) {
        // Pre-select resource when coming from resource card
        setSelectedResource(location.state.selectedResourceId);
      }
      
      // Restore scroll position
      if (location.state.scrollPosition) {
        setTimeout(() => {
          window.scrollTo(0, location.state.scrollPosition);
        }, 300);
      }
      
      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isPM, isRM]);

  useEffect(() => {
    if (isPM && selectedProject) {
      fetchMatches();
    } else if (isRM && selectedResource) {
      fetchMatches();
    }
  }, [selectedProject, selectedResource, isPM, isRM]);

  const fetchMyProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
      
      // Auto-select from URL if provided
      if (projectIdFromUrl && response.data.length > 0) {
        setSelectedProject(projectIdFromUrl);
      }
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  const fetchMyResources = async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch own resources
      const response = await axios.get(`${API}/resources/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let allResources = response.data;
      
      // If Operating Partner, also fetch represented freelancers' resources
      const OPERATING_PARTNER_ID = "81d4c9e3-139d-4ea0-ba86-e106396d69a8";
      if (user?.company_id === OPERATING_PARTNER_ID) {
        try {
          const representedResponse = await axios.get(`${API}/partner-manager/represented-freelancers`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Extract active resources from represented freelancers
          const representedResources = [];
          (representedResponse.data.freelancers || []).forEach(freelancer => {
            const activeResources = (freelancer.resources || []).filter(r => r.is_active);
            activeResources.forEach(resource => {
              representedResources.push({
                ...resource,
                // Mark as represented for visual distinction
                is_represented: true,
                freelancer_name: `${freelancer.profile?.first_name || ''} ${freelancer.profile?.last_name || ''}`.trim(),
                freelancer_company: freelancer.company?.name
              });
            });
          });
          
          // Combine own resources with represented freelancers' resources
          allResources = [...allResources, ...representedResources];
        } catch (err) {
          console.warn('Could not fetch represented freelancers:', err);
        }
      }
      
      setResources(allResources);
      
      // Auto-select from URL if provided
      if (resourceIdFromUrl && allResources.length > 0) {
        setSelectedResource(resourceIdFromUrl);
      }
    } catch (error) {
      toast.error('Failed to load resources');
    }
  };

  const fetchMatches = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    if (enableAI) setAiLoading(true);
    
    try {
      if (isPM && selectedProject) {
        // PM mode: find resources for project
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        if (enableAI) params.append('enable_ai', 'true');
        
        const response = await axios.get(`${API}/matching/detailed/${selectedProject}?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setProject(response.data.project);
        setMatches(response.data.matches);
      } else if (isRM && selectedResource) {
        // RM mode: find ALL projects for resource with detailed breakdown
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        if (enableAI) params.append('enable_ai', 'true');
        
        const response = await axios.get(`${API}/matching/detailed-projects-for-resource/${selectedResource}?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setResource(response.data.resource);
        setMatches(response.data.matches);
      }
    } catch (error) {
      // Check if this is a matching restriction error
      const errorDetail = error.response?.data?.detail;
      if (error.response?.status === 403 && errorDetail?.error === 'matching_restricted') {
        toast.error(errorDetail.message || 'Matching access is restricted on your current plan');
        // Could show a modal here offering upgrade options
      } else {
        toast.error('Failed to load matches');
      }
      console.error(error);
    } finally {
      setLoading(false);
      setAiLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return '#62D84E'; // Green
    if (score >= 40) return '#FFA500'; // Orange
    return '#EF4444'; // Red
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return 'Excellent Match';
    if (score >= 40) return 'Good Match';
    return 'Poor Match';
  };

  const handleContact = (resource, company) => {
    // Save current state before navigating
    navigateWithState(navigate, '/messages', {
      path: '/matching',
      selectedProject,
      pageName: 'Matching',
      // Pass resource info as location state
      locationState: { 
        targetCompanyId: company?.id,
        targetCompanyName: company?.name,
        resourceInfo: {
          id: resource.id,
          name: resource.name,
          role: resource.role
        }
      }
    });
    toast.success(`Opening conversation with ${company?.name || 'resource manager'}`);
  };

  // Bulk selection handlers
  const toggleResourceSelection = (resource, company) => {
    const resourceData = {
      id: resource.id,
      name: resource.name,
      role: resource.role,
      companyId: company.id,
      companyName: company.name
    };

    setSelectedResources(prev => {
      const isSelected = prev.some(r => r.id === resource.id);
      if (isSelected) {
        return prev.filter(r => r.id !== resource.id);
      } else {
        return [...prev, resourceData];
      }
    });
  };

  const clearSelection = () => {
    setSelectedResources([]);
    setSelectedProjects([]);
  };

  const isResourceSelected = (resourceId) => {
    return selectedResources.some(r => r.id === resourceId);
  };

  const isProjectSelected = (projectId) => {
    return selectedProjects.some(p => p.id === projectId);
  };

  const toggleProjectSelection = (project) => {
    setSelectedProjects(prev => {
      const exists = prev.some(p => p.id === project.id);
      if (exists) {
        return prev.filter(p => p.id !== project.id);
      } else {
        return [...prev, project];
      }
    });
  };

  const fetchFavorites = async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch both resource and project favorites
      const [resourcesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/favorites/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/favorites/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      
      // Combine resource and project favorites
      const resourceFavoriteIds = new Set(resourcesRes.data.map(fav => fav.resource_id));
      const projectFavoriteIds = projectsRes.data.map(fav => 
        fav.favorite?.project_id || fav.project?.id || fav.project_id
      );
      
      // Merge all favorites into one Set
      const allFavorites = new Set([
        ...resourceFavoriteIds,
        ...projectFavoriteIds
      ]);
      
      setFavorites(allFavorites);
    } catch (error) {
      console.error('Failed to load favorites');
    }
  };

  const toggleFavorite = async (itemId, itemName, itemType = 'resource') => {
    const token = localStorage.getItem('token');
    try {
      if (favorites.has(itemId)) {
        // Remove from favorites
        const endpoint = itemType === 'project' 
          ? `${API}/favorites/project/${itemId}` 
          : `${API}/favorites/resource/${itemId}`;
        
        await axios.delete(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        toast.success('Removed from favorites');
      } else {
        // Add to favorites
        const endpoint = itemType === 'project' 
          ? `${API}/favorites/project/${itemId}` 
          : `${API}/favorites/resource/${itemId}`;
        
        const response = await axios.post(endpoint, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => new Set(prev).add(itemId));
        
        if (itemType === 'resource') {
          // Show qualification modal only for resources
          setShowFavoriteModal({
            resourceId: itemId,
            resourceName: itemName,
            favoriteId: response.data.favorite_id || response.data.id
          });
        } else {
          toast.success('Added to favorites');
        }
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleSaveFavoriteQualification = async (data) => {
    const token = localStorage.getItem('token');
    try {
      if (showFavoriteModal?.favoriteId) {
        console.log('🔍 Saving favorite qualification:', data);
        console.log('🔍 Favorite ID:', showFavoriteModal.favoriteId);
        
        // Update tags
        if (data.tags && data.tags.length > 0) {
          const tagsResponse = await axios.put(`${API}/favorites/${showFavoriteModal.favoriteId}/tags`, {
            tags: data.tags
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('✅ Tags updated:', tagsResponse.data);
        }
        
        // Update notes
        if (data.notes) {
          const notesResponse = await axios.put(`${API}/favorites/${showFavoriteModal.favoriteId}/notes`, {
            private_notes: data.notes
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('✅ Notes updated:', notesResponse.data);
        }
        
        toast.success('Favorite qualified successfully');
      }
    } catch (error) {
      console.error('❌ Failed to update favorite qualification:', error);
      toast.error('Failed to save qualification');
    } finally {
      setShowFavoriteModal(null);
    }
  };

  // Filter and sort matches with advanced filters
  const filteredAndSortedMatches = useMemo(() => {
    let result = [...matches];
    
    // Filter by company type
    if (advancedFilters.companyTypes.length > 0) {
      result = result.filter(match => {
        const companyType = match.company?.company_type || match.project?.company?.company_type;
        return advancedFilters.companyTypes.includes(companyType);
      });
    }
    
    // NEW: Filter for AI-Augmented consultants only (PM view)
    if (advancedFilters.aiAugmentedOnly && isPM) {
      result = result.filter(match => match.resource?.is_ai_augmented === true);
    }
    
    // NEW: Filter for projects preferring AI-Augmented consultants (RM view)
    if (advancedFilters.prefersAiAugmented && isRM) {
      result = result.filter(match => match.project?.prefer_ai_augmented === true);
    }
    
    // Filter by internal mode
    if (advancedFilters.internalMode !== 'all') {
      result = result.filter(match => {
        const companyEmail = match.company?.contact_email || match.project?.company?.contact_email || '';
        const companyDomain = companyEmail.split('@')[1] || '';
        const companyId = match.company?.id || match.project?.company?.id;
        
        if (advancedFilters.internalMode === 'internal') {
          // Same domain = internal/group entities
          return companyDomain === userDomain;
        } else if (advancedFilters.internalMode === 'partners') {
          // Favorite partners
          return favoritePartners.some(p => p.company_id === companyId || p.id === companyId);
        }
        return true;
      });
    }
    
    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (advancedFilters.sortBy) {
        case 'score':
          // Backend returns 'total_score' field
          const scoreA = parseFloat(a.total_score) || parseFloat(a.match_score) || parseFloat(a.compatibility_score) || 0;
          const scoreB = parseFloat(b.total_score) || parseFloat(b.match_score) || parseFloat(b.compatibility_score) || 0;
          comparison = scoreA - scoreB;
          break;
        case 'price':
          // For PM: sort by resource daily rate
          // For RM: sort by project budget
          if (isPM) {
            const rateA = parseFloat(a.resource?.pricing_min_daily) || parseFloat(a.resource?.pricing_max_daily) || parseFloat(a.resource?.daily_rate) || 0;
            const rateB = parseFloat(b.resource?.pricing_min_daily) || parseFloat(b.resource?.pricing_max_daily) || parseFloat(b.resource?.daily_rate) || 0;
            comparison = rateA - rateB;
          } else {
            const budgetA = parseFloat(a.project?.price_amount) || parseFloat(a.project?.budget) || parseFloat(a.project?.daily_rate) || 0;
            const budgetB = parseFloat(b.project?.price_amount) || parseFloat(b.project?.budget) || parseFloat(b.project?.daily_rate) || 0;
            comparison = budgetA - budgetB;
          }
          break;
        case 'availability':
          // For PM: sort by resource availability date
          // For RM: sort by project start date
          if (isPM) {
            let dateA = new Date('9999-12-31');
            let dateB = new Date('9999-12-31');
            
            // "Available Now" should come first (use epoch 0)
            if (a.resource?.availability_status === 'Available Now') {
              dateA = new Date(0);
            } else if (a.resource?.available_from) {
              dateA = new Date(a.resource.available_from);
            }
            
            if (b.resource?.availability_status === 'Available Now') {
              dateB = new Date(0);
            } else if (b.resource?.available_from) {
              dateB = new Date(b.resource.available_from);
            }
            
            comparison = dateA.getTime() - dateB.getTime();
          } else {
            const dateA = a.project?.start_date ? new Date(a.project.start_date) : new Date('9999-12-31');
            const dateB = b.project?.start_date ? new Date(b.project.start_date) : new Date('9999-12-31');
            comparison = dateA.getTime() - dateB.getTime();
          }
          break;
        default:
          // Backend returns 'total_score' field
          const defaultScoreA = parseFloat(a.total_score) || parseFloat(a.match_score) || parseFloat(a.compatibility_score) || 0;
          const defaultScoreB = parseFloat(b.total_score) || parseFloat(b.match_score) || parseFloat(b.compatibility_score) || 0;
          comparison = defaultScoreA - defaultScoreB;
      }
      
      return advancedFilters.sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return result;
  }, [matches, advancedFilters, isPM, userDomain, favoritePartners]);

  const handlePreReserveClick = (resourceId, resourceName) => {
    if (!selectedProject) {
      toast.error('Please select a project first');
      return;
    }
    
    setShowPreReserveModal({
      resourceId,
      resourceName,
      projectId: selectedProject,
      projectTitle: project?.title
    });
  };

  const handleConfirmPreReserve = async (customMessage) => {
    const token = localStorage.getItem('token');
    try {
      // Determine if this is RM applying to a project or PM reserving a resource
      const isRMApplication = showPreReserveModal?.isRMInitiated;
      
      const endpoint = isRMApplication 
        ? `${API}/pre-reservations/apply`
        : `${API}/pre-reservations`;
      
      await axios.post(endpoint, null, {
        params: { 
          project_id: showPreReserveModal.projectId, 
          resource_id: showPreReserveModal.resourceId,
          custom_message: customMessage || undefined
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const successMessage = isRMApplication 
        ? 'Application submitted successfully! The PM will review your resource.'
        : 'Pre-reservation request sent';
      
      toast.success(successMessage);
      setShowPreReserveModal(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 
        (showPreReserveModal?.isRMInitiated ? 'Failed to submit application' : 'Failed to send pre-reservation');
      toast.error(errorMsg);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>
          <TrendingUp className="inline w-8 h-8 mr-2" style={{ color: '#62D84E' }} />
          Advanced Matching
        </h1>
        <p className="text-gray-600 mt-2">
          {isPM && 'Find the perfect consultants with detailed compatibility scores'}
          {isRM && 'Find the perfect projects for your resources'}
        </p>
      </div>

      {/* Project Selection (PM) or Resource Selection (RM) - Modernized */}
      <Card className="mb-6 bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              {isPM ? (
                <Briefcase className="w-5 h-5 text-blue-600" />
              ) : (
                <Users className="w-5 h-5 text-emerald-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">
                {isPM ? 'Select Your Project' : 'Select Your Resource'}
              </h3>
              <p className="text-sm text-gray-500">
                {isPM ? 'Choose a project to find matching consultants' : 'Choose a resource to find matching projects'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* PM: Select Project */}
            {isPM && (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Project</Label>
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all shadow-sm hover:border-gray-300"
                  >
                    <option value="">-- Select a project --</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.project_id} - {p.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                {project && (
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-800">{project.title}</h3>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">{project.project_id}</span>
                      </div>
                      {project.is_activated && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                          <Star className="w-3 h-3 fill-current" /> Active
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                        {project.city || 'N/A'}, {project.country || 'N/A'}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="w-4 h-4 mr-2 text-gray-400" />
                        {project.number_of_consultants || 1} consultant{(project.number_of_consultants || 1) > 1 ? 's' : ''} needed
                      </div>
                      {project.price_amount && (
                        <div className="flex items-center text-sm font-semibold text-gray-800">
                          <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
                          {project.currency} {project.price_amount}/{project.price_type}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* RM: Select Resource */}
            {isRM && (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Resource</Label>
                  <select
                    value={selectedResource}
                    onChange={(e) => setSelectedResource(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all shadow-sm hover:border-gray-300"
                  >
                    <option value="">-- Select a resource --</option>
                    {/* Group own resources first */}
                    {resources.filter(r => !r.is_represented).length > 0 && (
                      <optgroup label="My Resources">
                        {resources.filter(r => !r.is_represented).map(r => (
                          <option key={r.id} value={r.id}>
                            {r.resource_id} - {r.first_name} {r.last_name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {/* Then represented freelancers */}
                    {resources.filter(r => r.is_represented).length > 0 && (
                      <optgroup label="Freelancers I Represent">
                        {resources.filter(r => r.is_represented).map(r => (
                          <option key={r.id} value={r.id}>
                            🟣 {r.first_name} {r.last_name} ({r.freelancer_company || 'Represented'})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                
                {/* RM: Resource Info Panel - Show selected resource details */}
                {selectedResource && (
                  (() => {
                    const selectedRes = resources.find(r => r.id === selectedResource) || resource;
                    if (!selectedRes) return null;
                    return (
                      <div className={`p-5 rounded-xl border shadow-sm ${selectedRes.is_represented ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-800">
                              {selectedRes.first_name} {selectedRes.last_name}
                            </h3>
                            {selectedRes.is_represented && (
                              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                Represented
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {/* Role, Experience & Products - Key info */}
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            {/* Roles acronyms */}
                            {(selectedRes.roles && selectedRes.roles.length > 0 
                              ? selectedRes.roles.slice(0, 2)
                              : [selectedRes.role]
                            ).filter(Boolean).map((role, idx) => {
                              const roleAcronyms = {
                                'Developer': 'Dev', 'Technical Architect': 'TA', 'Solution Architect': 'SA',
                                'Business Analyst': 'BA', 'Project Manager': 'PM', 'Technical Consultant': 'TC',
                                'Functional Consultant': 'FC', 'Implementation Specialist': 'IS'
                              };
                              const acronym = roleAcronyms[role] || role?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3) || 'Cons';
                              return (
                                <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {acronym}
                                </span>
                              );
                            })}
                            {/* Years experience */}
                            <span className="text-gray-600 text-xs">
                              {selectedRes.servicenow_experience_years || selectedRes.experience_years || 0}y exp
                            </span>
                          </div>
                          
                          {/* Products mastered */}
                          {selectedRes.servicenow_products && selectedRes.servicenow_products.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {selectedRes.servicenow_products.slice(0, 4).map((product, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                                  {typeof product === 'string' ? product.toUpperCase() : product}
                                </span>
                              ))}
                              {selectedRes.servicenow_products.length > 4 && (
                                <span className="text-xs text-gray-400">+{selectedRes.servicenow_products.length - 4}</span>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                            {selectedRes.work_city || selectedRes.city || 'N/A'}, {selectedRes.work_country || selectedRes.country || 'N/A'}
                          </div>
                          {selectedRes.daily_rate && (
                            <div className="flex items-center text-sm font-semibold text-gray-800">
                              <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
                              {selectedRes.currency} {selectedRes.daily_rate}/day
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters (PM only) */}
      {isPM && selectedProject && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Minimum Score (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.min_score}
                  onChange={(e) => setFilters({...filters, min_score: e.target.value})}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Seniority</Label>
                <select
                  value={filters.seniority}
                  onChange={(e) => setFilters({...filters, seniority: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">All</option>
                  <option value="junior">Junior</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="medior">Medior</option>
                  <option value="senior">Senior</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
              
              <div>
                <Label>Availability</Label>
                <select
                  value={filters.availability}
                  onChange={(e) => setFilters({...filters, availability: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">All</option>
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                </select>
              </div>
              
              <div>
                <Label>Min Daily Rate (€)</Label>
                <Input
                  type="number"
                  value={filters.min_rate}
                  onChange={(e) => setFilters({...filters, min_rate: e.target.value})}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Max Daily Rate (€)</Label>
                <Input
                  type="number"
                  value={filters.max_rate}
                  onChange={(e) => setFilters({...filters, max_rate: e.target.value})}
                  placeholder="No limit"
                />
              </div>
              
              <div>
                <Label>Location</Label>
                <Input
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                  placeholder="City or country"
                />
              </div>
            </div>
            
            {/* Advanced Filters Toggle */}
            <div className="mt-4 border-t pt-4">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <Filter className="w-4 h-4" />
                Advanced Filters
                {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showAdvancedFilters && (
                <div className="mt-4 space-y-4">
                  {/* Company Type Filter */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4" />
                      Company Type
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {COMPANY_TYPES.map(type => (
                        <button
                          key={type.value}
                          onClick={() => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              companyTypes: prev.companyTypes.includes(type.value)
                                ? prev.companyTypes.filter(t => t !== type.value)
                                : [...prev.companyTypes, type.value]
                            }));
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            advancedFilters.companyTypes.includes(type.value)
                              ? 'bg-emerald-100 border-emerald-500 text-emerald-700'
                              : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* AI-Augmented Filter (PM view only) */}
                  {isPM && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4" />
                        Consultant Type
                      </Label>
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, aiAugmentedOnly: !prev.aiAugmentedOnly }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.aiAugmentedOnly
                            ? 'bg-purple-100 border-purple-500 text-purple-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Zap className={`w-4 h-4 ${advancedFilters.aiAugmentedOnly ? 'text-purple-600' : 'text-gray-400'}`} />
                        AI-Augmented Only
                      </button>
                      <p className="text-xs text-gray-500 mt-1">
                        Filter to show only consultants using AI tools (GitHub Copilot, Now Assist, etc.)
                      </p>
                    </div>
                  )}
                  
                  {/* AI-Augmented Preference Filter (RM view only) */}
                  {isRM && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4" />
                        Project AI Preference
                      </Label>
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, prefersAiAugmented: !prev.prefersAiAugmented }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.prefersAiAugmented
                            ? 'bg-purple-100 border-purple-500 text-purple-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Zap className={`w-4 h-4 ${advancedFilters.prefersAiAugmented ? 'text-purple-600' : 'text-gray-400'}`} />
                        Prefers AI-Augmented
                      </button>
                      <p className="text-xs text-gray-500 mt-1">
                        Show only projects that prefer AI-augmented consultants
                      </p>
                    </div>
                  )}
                  
                  {/* Sort Options */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort By
                      </Label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={advancedFilters.sortBy}
                        onChange={(e) => setAdvancedFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                      >
                        <option value="score">Match Score</option>
                        <option value="price">{isPM ? 'Daily Rate' : 'Budget'}</option>
                        <option value="availability">{isPM ? 'Availability Date' : 'Project Start Date'}</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label className="mb-2">Order</Label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={advancedFilters.sortOrder}
                        onChange={(e) => setAdvancedFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                      >
                        <option value="desc">{advancedFilters.sortBy === 'price' ? 'Highest first' : advancedFilters.sortBy === 'availability' ? 'Soonest first' : 'Best first'}</option>
                        <option value="asc">{advancedFilters.sortBy === 'price' ? 'Lowest first' : advancedFilters.sortBy === 'availability' ? 'Latest first' : 'Lowest first'}</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Internal/External Mode */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4" />
                      Scope
                    </Label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, internalMode: 'all' }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.internalMode === 'all'
                            ? 'bg-emerald-100 border-emerald-500 text-emerald-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Globe className="w-4 h-4" />
                        All Companies
                      </button>
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, internalMode: 'internal' }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.internalMode === 'internal'
                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                        title={`Companies with @${userDomain} domain`}
                      >
                        <Lock className="w-4 h-4" />
                        Internal ({userDomain || 'your domain'})
                      </button>
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, internalMode: 'partners' }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.internalMode === 'partners'
                            ? 'bg-purple-100 border-purple-500 text-purple-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Heart className="w-4 h-4" />
                        Favorite Partners ({favoritePartners.length})
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* AI Matching Toggle */}
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">AI-Powered Matching</p>
                    <p className="text-sm text-gray-600">
                      Deep semantic analysis of project requirements and resource profiles
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableAI}
                    onChange={(e) => setEnableAI(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              {enableAI && (
                <p className="mt-2 text-xs text-purple-600 flex items-center gap-1">
                  <Brain className="w-3 h-3" />
                  GPT analyzes descriptions, CVs, and hidden compatibility factors
                </p>
              )}
            </div>
            
            <div className="mt-4 flex gap-2">
              <Button onClick={fetchMatches} style={{ backgroundColor: '#62D84E' }} disabled={loading}>
                {aiLoading ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                    AI Analyzing...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {enableAI ? 'Search with AI' : 'Search Matches'}
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setFilters({
                    min_score: 0,
                    certifications: '',
                    seniority: '',
                    min_rate: '',
                    max_rate: '',
                    location: '',
                    availability: ''
                  });
                  setAdvancedFilters({
                    companyTypes: [],
                    sortBy: 'score',
                    sortOrder: 'desc',
                    internalMode: 'all'
                  });
                }}
              >
                Clear All Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters (RM only) */}
      {isRM && selectedResource && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Minimum Score (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.min_score}
                  onChange={(e) => setFilters({...filters, min_score: e.target.value})}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Location</Label>
                <Input
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                  placeholder="City or country"
                />
              </div>
              
              <div>
                <Label>Min Budget (€)</Label>
                <Input
                  type="number"
                  value={filters.min_rate}
                  onChange={(e) => setFilters({...filters, min_rate: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>
            
            {/* Advanced Filters Toggle for RM */}
            <div className="mt-4 border-t pt-4">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <Filter className="w-4 h-4" />
                Advanced Filters
                {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showAdvancedFilters && (
                <div className="mt-4 space-y-4">
                  {/* Company Type Filter */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4" />
                      Company Type
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {COMPANY_TYPES.map(type => (
                        <button
                          key={type.value}
                          onClick={() => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              companyTypes: prev.companyTypes.includes(type.value)
                                ? prev.companyTypes.filter(t => t !== type.value)
                                : [...prev.companyTypes, type.value]
                            }));
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            advancedFilters.companyTypes.includes(type.value)
                              ? 'bg-emerald-100 border-emerald-500 text-emerald-700'
                              : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Sort Options */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort By
                      </Label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={advancedFilters.sortBy}
                        onChange={(e) => setAdvancedFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                      >
                        <option value="score">Match Score</option>
                        <option value="price">Budget</option>
                        <option value="availability">Project Start Date</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label className="mb-2">Order</Label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={advancedFilters.sortOrder}
                        onChange={(e) => setAdvancedFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                      >
                        <option value="desc">{advancedFilters.sortBy === 'price' ? 'Highest first' : advancedFilters.sortBy === 'availability' ? 'Soonest first' : 'Best first'}</option>
                        <option value="asc">{advancedFilters.sortBy === 'price' ? 'Lowest first' : advancedFilters.sortBy === 'availability' ? 'Latest first' : 'Lowest first'}</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Internal/External Mode */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4" />
                      Scope
                    </Label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, internalMode: 'all' }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.internalMode === 'all'
                            ? 'bg-emerald-100 border-emerald-500 text-emerald-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Globe className="w-4 h-4" />
                        All Companies
                      </button>
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, internalMode: 'internal' }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.internalMode === 'internal'
                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                        title={`Companies with @${userDomain} domain`}
                      >
                        <Lock className="w-4 h-4" />
                        Internal ({userDomain || 'your domain'})
                      </button>
                      <button
                        onClick={() => setAdvancedFilters(prev => ({ ...prev, internalMode: 'partners' }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          advancedFilters.internalMode === 'partners'
                            ? 'bg-purple-100 border-purple-500 text-purple-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Heart className="w-4 h-4" />
                        Favorite Partners ({favoritePartners.length})
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* AI Matching Toggle */}
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">AI-Powered Matching</p>
                    <p className="text-sm text-gray-600">
                      Deep semantic analysis of project requirements and resource profiles
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableAI}
                    onChange={(e) => setEnableAI(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              {enableAI && (
                <p className="mt-2 text-xs text-purple-600 flex items-center gap-1">
                  <Brain className="w-3 h-3" />
                  GPT analyzes descriptions, CVs, and hidden compatibility factors
                </p>
              )}
            </div>
            
            <div className="mt-4 flex gap-2">
              <Button onClick={fetchMatches} style={{ backgroundColor: '#62D84E' }} disabled={loading}>
                {aiLoading ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                    AI Analyzing...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {enableAI ? 'Search with AI' : 'Search Matches'}
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setFilters({
                    min_score: 0,
                    certifications: '',
                    seniority: '',
                    min_rate: '',
                    max_rate: '',
                    location: '',
                    availability: ''
                  });
                  setAdvancedFilters({
                    companyTypes: [],
                    sortBy: 'score',
                    sortOrder: 'desc',
                    internalMode: 'all'
                  });
                }}
              >
                Clear All Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
        </div>
      ) : (isPM && selectedProject && matches.length > 0) || (isRM && selectedResource && matches.length > 0) ? (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <span className="text-gray-600">
              {isPM && `${filteredAndSortedMatches.length} consultant${filteredAndSortedMatches.length !== 1 ? 's' : ''} found`}
              {isRM && `${filteredAndSortedMatches.length} project${filteredAndSortedMatches.length !== 1 ? 's' : ''} found`}
              {filteredAndSortedMatches.length !== matches.length && (
                <span className="text-gray-400 ml-2">
                  (filtered from {matches.length})
                </span>
              )}
            </span>
            {advancedFilters.companyTypes.length > 0 || advancedFilters.internalMode !== 'all' ? (
              <span className="text-sm text-emerald-600">
                Filters active
              </span>
            ) : null}
          </div>
          
          <div className="space-y-4">
            {isPM && filteredAndSortedMatches.map((match, idx) => (
              <Card 
                key={idx} 
                className={`hover:shadow-lg transition-shadow ${
                  match.match_score === 100 ? 'border-2 border-green-500' : ''
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox for selection */}
                    <button
                      onClick={() => toggleResourceSelection(match.resource, match.company)}
                      className="mt-1 flex-shrink-0 hover:opacity-70 transition-opacity"
                    >
                      {isResourceSelected(match.resource.id) ? (
                        <CheckSquare className="w-6 h-6 text-blue-600" />
                      ) : (
                        <Square className="w-6 h-6 text-gray-400" />
                      )}
                    </button>

                    {/* Resource Info */}
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-3">
                        <h3 className="text-xl font-semibold" style={{ color: '#1B3240' }}>
                          {match.resource.name}
                        </h3>
                        {match.match_score === 100 && (
                          <span className="px-3 py-1 text-sm rounded-full font-semibold flex items-center gap-1" 
                            style={{ backgroundColor: '#62D84E', color: 'white' }}>
                            <span>🎯</span> Perfect Match
                          </span>
                        )}
                        {match.resource.is_ai_augmented && (
                          <span className="px-2 py-1 text-xs rounded-full font-medium flex items-center gap-1 bg-purple-100 text-purple-700 border border-purple-200">
                            <Zap className="w-3 h-3" /> AI-Augmented
                          </span>
                        )}
                        {match.resource.security_clearance && match.resource.security_clearance !== 'none' && (
                          <span className="px-2 py-1 text-xs rounded-full font-medium flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-200" title={`Clearance: ${match.resource.security_clearance} (${match.resource.clearance_country || 'N/A'})`}>
                            <Shield className="w-3 h-3" /> Clearance
                          </span>
                        )}
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {match.resource.role}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                          {match.resource.seniority}
                        </span>
                      </div>
                      
                      {/* ServiceNow Products */}
                      {match.resource.servicenow_products && match.resource.servicenow_products.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {match.resource.servicenow_products.slice(0, 5).map(productId => {
                            const product = SERVICENOW_PRODUCTS.find(p => p.id === productId);
                            return product && (
                              <span key={productId} className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-100" title={product.fullName}>
                                {product.name}
                              </span>
                            );
                          })}
                          {match.resource.servicenow_products.length > 5 && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              +{match.resource.servicenow_products.length - 5}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="text-sm text-gray-600 mb-3">
                        <p className="mb-1">
                          <MapPin className="inline w-4 h-4 mr-1" />
                          <button
                            onClick={() => navigateWithState(navigate, `/company/${match.company?.id}`, {
                              path: '/matching',
                              selectedProject,
                              pageName: 'Matching'
                            })}
                            className="hover:text-blue-600 hover:underline font-medium"
                          >
                            {match.company?.is_anonymous ? (
                              <span className="inline-flex items-center gap-1 text-gray-500 italic">
                                <Lock className="w-3.5 h-3.5" />
                                {match.company?.name || 'Confidential Partner'}
                              </span>
                            ) : (match.company?.name || 'Unknown Company')}
                          </button> • {match.resource.work_city || match.resource.city || 'N/A'}, {match.resource.work_country || match.resource.country || 'N/A'}
                          {match.score_breakdown?.location?.distance_km > 0 && (
                            <span className="text-blue-600 ml-1">
                              ({match.score_breakdown.location.distance_km} km)
                            </span>
                          )}
                        </p>
                        <p>
                          <DollarSign className="inline w-4 h-4 mr-1" />
                          {match.resource.currency} {match.resource.daily_rate}/day • {match.resource.availability_status}
                        </p>
                      </div>

                      {/* Score Badge */}
                      <div className="mb-3">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="text-2xl font-bold"
                            style={{ color: getScoreColor(match.total_score) }}
                          >
                            {match.total_score}%
                          </div>
                          <div>
                            <p className="text-sm font-medium" style={{ color: getScoreColor(match.total_score) }}>
                              {getScoreLabel(match.total_score)}
                            </p>
                            <p className="text-xs text-gray-500">Compatibility Score</p>
                          </div>
                        </div>
                      </div>

                      {/* Score Breakdown Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowBreakdown(showBreakdown === idx ? null : idx)}
                      >
                        <Award className="w-4 h-4 mr-2" />
                        {showBreakdown === idx ? 'Hide' : 'Show'} Score Breakdown
                      </Button>

                      {/* Detailed Breakdown */}
                      {showBreakdown === idx && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                          {Object.entries(match.score_breakdown).map(([criterion, data]) => (
                            <div key={criterion}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium capitalize">{criterion}</span>
                                <span className="text-sm text-gray-600">
                                  {data.score}% (weight: {data.weight})
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full transition-all"
                                  style={{
                                    width: `${data.score}%`,
                                    backgroundColor: getScoreColor(data.score)
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="ml-4 flex flex-col gap-2">
                      <ViewResourceProfileButton 
                        resourceId={match.resource.id}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        navigationState={{
                          path: '/matching',
                          selectedProject,
                          pageName: 'Matching'
                        }}
                      />
                      
                      <Button 
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleFavorite(match.resource.id, match.resource.name)}
                        className="w-full"
                      >
                        <Heart className={`w-4 h-4 mr-2 ${favorites.has(match.resource.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                        {favorites.has(match.resource.id) ? 'Favorited' : 'Favorite'}
                      </Button>
                      
                      <Button 
                        size="sm" 
                        style={{ backgroundColor: '#62D84E' }}
                        onClick={() => handlePreReserveClick(match.resource.id, match.resource.name)}
                        className="w-full"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Pre-Reserve
                      </Button>
                      
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleContact(match.resource, match.company)}
                        className="w-full"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Contact
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* RM Mode: Display Projects with Score Breakdown */}
            {isRM && filteredAndSortedMatches.map((match, idx) => (
              <Card 
                key={idx} 
                className={`hover:shadow-lg transition-shadow ${
                  match.compatibility_score >= 80 ? 'border-2 border-green-500' : ''
                } ${isProjectSelected(match.project.id) ? 'ring-2 ring-blue-500' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox for multi-selection */}
                    <div className="flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        checked={isProjectSelected(match.project.id)}
                        onChange={() => toggleProjectSelection({
                          id: match.project.id,
                          title: match.project.title,
                          company_id: match.company?.id,
                          company_name: match.company?.name
                        })}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                    </div>
                    
                    {/* Project Info */}
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-3">
                        <h3 className="text-xl font-semibold" style={{ color: '#1B3240' }}>
                          {match.project.title}
                        </h3>
                        {match.compatibility_score >= 80 && (
                          <span className="px-3 py-1 text-sm rounded-full font-semibold flex items-center gap-1" 
                            style={{ backgroundColor: '#62D84E', color: 'white' }}>
                            <span>🎯</span> Great Match
                          </span>
                        )}
                        {match.project.prefer_ai_augmented && (
                          <span className="px-2 py-1 text-xs rounded-full font-medium flex items-center gap-1 bg-purple-100 text-purple-700 border border-purple-200" title="Prefers AI-Augmented consultants">
                            <Zap className="w-3 h-3" /> AI-Preferred
                          </span>
                        )}
                        {match.project.has_security_requirement && (
                          <span className="px-2 py-1 text-xs rounded-full font-medium flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-200" title="Has security requirements">
                            <Shield className="w-3 h-3" /> Security Req.
                          </span>
                        )}
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {match.project.project_id}
                        </span>
                      </div>
                      
                      {/* ServiceNow Products */}
                      {match.project.servicenow_products && match.project.servicenow_products.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {match.project.servicenow_products.slice(0, 4).map(productId => {
                            const product = SERVICENOW_PRODUCTS.find(p => p.id === productId);
                            return product && (
                              <span key={productId} className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-100" title={product.fullName}>
                                {product.name}
                              </span>
                            );
                          })}
                          {match.project.servicenow_products.length > 4 && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              +{match.project.servicenow_products.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Company Info */}
                      {match.company && (
                        <div className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Company:</span>{' '}
                          <button
                            onClick={() => navigateWithState(navigate, `/company/${match.company.id}`, {
                              path: '/matching',
                              selectedResource,
                              pageName: 'Matching'
                            })}
                            className="text-blue-600 hover:underline hover:text-blue-800"
                          >
                            {match.company?.is_anonymous ? (
                              <span className="inline-flex items-center gap-1 text-gray-500 italic">
                                <Lock className="w-3.5 h-3.5" />
                                {match.company?.name || 'Confidential Partner'}
                              </span>
                            ) : match.company.name}
                          </button>
                        </div>
                      )}
                      
                      <div className="text-sm text-gray-600 mb-3 space-y-1">
                        <p>
                          <MapPin className="inline w-4 h-4 mr-1" />
                          {match.project.city}, {match.project.country}
                          {match.score_breakdown?.location?.distance_km > 0 && (
                            <span className="text-blue-600 ml-1">
                              ({match.score_breakdown.location.distance_km} km)
                            </span>
                          )}
                          {match.project.allows_remote && <span className="ml-2 text-green-600">(Remote OK)</span>}
                        </p>
                        {/* Hide budget if PM has enabled hide_budget option */}
                        {!match.project.hide_budget && (match.project.min_daily_rate || match.project.max_daily_rate) && (
                          <p>
                            <DollarSign className="inline w-4 h-4 mr-1" />
                            Budget: {match.project.currency || 'EUR'} {match.project.min_daily_rate || 0} - {match.project.max_daily_rate || 'N/A'}/day
                          </p>
                        )}
                        {match.project.hide_budget && (
                          <p className="text-gray-400 italic flex items-center">
                            <Lock className="inline w-4 h-4 mr-1" />
                            Budget hidden by PM
                          </p>
                        )}
                        {match.project.duration_value && (
                          <p>
                            <Calendar className="inline w-4 h-4 mr-1" />
                            Duration: {match.project.duration_value} {match.project.duration_unit}
                          </p>
                        )}
                      </div>
                      
                      {/* Description */}
                      {match.project.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {match.project.description}
                        </p>
                      )}
                      
                      {/* Overall Match Score */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">Overall Match Score</span>
                          <span className="text-lg font-bold" style={{ color: getScoreColor(match.compatibility_score) }}>
                            {match.compatibility_score}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="h-3 rounded-full transition-all"
                            style={{
                              width: `${match.compatibility_score}%`,
                              backgroundColor: getScoreColor(match.compatibility_score)
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Score Breakdown Toggle */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBreakdown(showBreakdown === idx ? null : idx)}
                        className="mb-3 text-blue-600 hover:text-blue-800"
                      >
                        {showBreakdown === idx ? '▲ Hide Score Details' : '▼ Show Score Details'}
                      </Button>
                      
                      {/* Detailed Score Breakdown */}
                      {showBreakdown === idx && match.score_breakdown && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                          <h4 className="font-semibold text-sm mb-3 text-gray-700">Score Breakdown</h4>
                          <div className="space-y-3">
                            {Object.entries(match.score_breakdown).map(([key, data]) => (
                              <div key={key} className="flex items-center gap-3">
                                <div className="w-24 text-xs font-medium text-gray-600 capitalize">{key}</div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                      <div
                                        className="h-2 rounded-full transition-all"
                                        style={{
                                          width: `${data.score}%`,
                                          backgroundColor: data.score >= 70 ? '#62D84E' : data.score >= 40 ? '#F59E0B' : '#EF4444'
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs font-bold w-12 text-right" style={{ color: data.score >= 70 ? '#16A34A' : data.score >= 40 ? '#D97706' : '#DC2626' }}>
                                      {data.score}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-xs text-gray-500 mt-0.5">{data.description}</p>
                                    {/* Show distance for location criterion */}
                                    {key === 'location' && data.distance_km !== null && data.distance_km !== undefined && (
                                      <span className="text-xs text-blue-600 font-medium mt-0.5">
                                        📏 {data.distance_km} km
                                      </span>
                                    )}
                                  </div>
                                  {data.matched && data.matched.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {data.matched.slice(0, 3).map((item, i) => (
                                        <span key={i} className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                          ✓ {item}
                                        </span>
                                      ))}
                                      {data.matched.length > 3 && (
                                        <span className="text-xs text-gray-400">+{data.matched.length - 3} more</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* AI Insights Section */}
                      {match.ai_analysis?.ai_analyzed && showBreakdown === idx && (
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-4 border border-purple-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-4 h-4 text-purple-600" />
                            <h4 className="font-semibold text-sm text-purple-800">AI Analysis</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              match.ai_analysis.ai_confidence === 'high' ? 'bg-green-100 text-green-700' :
                              match.ai_analysis.ai_confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {match.ai_analysis.ai_confidence} confidence
                            </span>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-3">
                            {/* Traditional vs AI Score */}
                            <div className="flex items-center gap-2 bg-white rounded-lg p-2 border">
                              <div className="text-center flex-1">
                                <p className="text-xs text-gray-500">Traditional</p>
                                <p className="text-lg font-bold text-gray-700">{match.ai_analysis.traditional_score}%</p>
                              </div>
                              <div className="text-gray-300">+</div>
                              <div className="text-center flex-1">
                                <p className="text-xs text-purple-500">AI Score</p>
                                <p className="text-lg font-bold text-purple-600">{match.ai_analysis.ai_score}%</p>
                              </div>
                              <div className="text-gray-300">=</div>
                              <div className="text-center flex-1 bg-purple-100 rounded p-1">
                                <p className="text-xs text-purple-600">Combined</p>
                                <p className="text-lg font-bold text-purple-700">{match.ai_analysis.combined_score}%</p>
                              </div>
                            </div>
                            
                            {/* Match Quality Badge */}
                            <div className="flex items-center justify-center gap-2 bg-white rounded-lg p-2 border">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                match.ai_analysis.ai_insights?.match_quality === 'excellent' ? 'bg-green-100 text-green-700' :
                                match.ai_analysis.ai_insights?.match_quality === 'strong' ? 'bg-emerald-100 text-emerald-700' :
                                match.ai_analysis.ai_insights?.match_quality === 'good' ? 'bg-blue-100 text-blue-700' :
                                match.ai_analysis.ai_insights?.match_quality === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {match.ai_analysis.ai_insights?.match_quality?.toUpperCase() || 'N/A'} MATCH
                              </span>
                            </div>
                          </div>
                          
                          {/* Key Strengths */}
                          {match.ai_analysis.ai_insights?.key_strengths?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-green-700 flex items-center gap-1 mb-1">
                                <CheckCircle className="w-3 h-3" /> Key Strengths
                              </p>
                              <ul className="space-y-1">
                                {match.ai_analysis.ai_insights.key_strengths.slice(0, 3).map((strength, i) => (
                                  <li key={i} className="text-xs text-gray-700 pl-4 relative">
                                    <span className="absolute left-0 text-green-500">✓</span>
                                    {strength}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Potential Gaps */}
                          {match.ai_analysis.ai_insights?.potential_gaps?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-1">
                                <AlertTriangle className="w-3 h-3" /> Potential Gaps
                              </p>
                              <ul className="space-y-1">
                                {match.ai_analysis.ai_insights.potential_gaps.slice(0, 2).map((gap, i) => (
                                  <li key={i} className="text-xs text-gray-700 pl-4 relative">
                                    <span className="absolute left-0 text-amber-500">⚠</span>
                                    {gap}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Hidden Insights */}
                          {match.ai_analysis.ai_insights?.hidden_insights?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-purple-700 flex items-center gap-1 mb-1">
                                <Lightbulb className="w-3 h-3" /> Hidden Insights
                              </p>
                              <ul className="space-y-1">
                                {match.ai_analysis.ai_insights.hidden_insights.slice(0, 2).map((insight, i) => (
                                  <li key={i} className="text-xs text-gray-700 pl-4 relative">
                                    <span className="absolute left-0 text-purple-500">💡</span>
                                    {insight}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* AI Recommendation */}
                          {match.ai_analysis.ai_insights?.recommendation && (
                            <div className="mt-3 p-2 bg-white rounded border border-purple-200">
                              <p className="text-xs text-purple-800">
                                <span className="font-semibold">AI Recommendation: </span>
                                {match.ai_analysis.ai_insights.recommendation}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Matching Skills & Certifications Summary */}
                      {(match.matching_skills?.length > 0 || match.matching_certifications?.length > 0) && showBreakdown !== idx && (
                        <div className="mb-3">
                          {match.matching_skills?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              <span className="text-xs font-semibold text-gray-700">Matching Skills:</span>
                              {match.matching_skills.slice(0, 5).map((skill, i) => (
                                <span key={i} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md border border-green-200">
                                  {skill}
                                </span>
                              ))}
                              {match.matching_skills.length > 5 && (
                                <span className="text-xs text-gray-400">+{match.matching_skills.length - 5} more</span>
                              )}
                            </div>
                          )}
                          
                          {match.matching_certifications?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              <span className="text-xs font-semibold text-gray-700">Matching Certifications:</span>
                              {match.matching_certifications.map((cert, i) => (
                                <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200">
                                  {cert}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="ml-4 flex flex-col gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Save state before navigating to project details
                          navigateWithState(navigate, `/projects/${match.project.id}`, {
                            path: '/matching',
                            selectedResource,
                            scrollPosition: window.scrollY,
                            pageName: 'Matching'
                          });
                        }}
                        className="w-full"
                      >
                        View Project
                      </Button>
                      
                      <Button 
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleFavorite(match.project.id, match.project.title, 'project')}
                        className="w-full"
                      >
                        <Heart className={`w-4 h-4 mr-2 ${favorites.has(match.project.id) ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'}`} />
                        {favorites.has(match.project.id) ? 'Favorited' : 'Favorite'}
                      </Button>
                      
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Navigate to messages with pre-filled context
                          navigateWithState(navigate, '/messages', {
                            path: '/matching',
                            selectedResource,
                            pageName: 'Matching',
                            locationState: { 
                              targetCompanyId: match.company?.id,
                              targetCompanyName: match.company?.name,
                              projectInfo: {
                                id: match.project.id,
                                title: match.project.title
                              },
                              resourceInfo: resource ? {
                                id: resource.id,
                                name: `${resource.first_name} ${resource.last_name}`,
                                role: resource.role || resource.roles?.[0]
                              } : null,
                              prefilledMessage: `Hello,\n\nI am reaching out regarding the project "${match.project.title}".\n\nI have a resource, ${resource?.first_name} ${resource?.last_name}, who matches ${match.compatibility_score}% with your project requirements.\n\nWould you be interested in discussing this opportunity?\n\nBest regards`
                            }
                          });
                          toast.success(`Opening conversation about ${match.project.title}`);
                        }}
                        className="w-full"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Contact PM
                      </Button>
                      
                      {/* Apply / Pre-reservation button for RM */}
                      <Button 
                        size="sm"
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                        onClick={() => {
                          setShowPreReserveModal({
                            resourceId: selectedResource,
                            resourceName: resource ? `${resource.first_name} ${resource.last_name}` : 'Resource',
                            projectId: match.project.id,
                            projectTitle: match.project.title,
                            toCompanyId: match.company?.id,
                            toCompanyName: match.company?.name,
                            isRMInitiated: true
                          });
                        }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Apply for Project
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (isPM && selectedProject && !loading) || (isRM && selectedResource && !loading) ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">
              {isPM && 'No consultants match your criteria'}
              {isRM && 'No projects match this resource'}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {isPM && 'Try adjusting your filters'}
              {isRM && 'Keep checking back for new opportunities'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Select a project to see matching consultants</p>
          </CardContent>
        </Card>
      )}
      
      {/* Favorite Qualification Modal */}
      <FavoriteQualificationModal
        isOpen={!!showFavoriteModal}
        onClose={() => setShowFavoriteModal(null)}
        onSave={handleSaveFavoriteQualification}
        resourceName={showFavoriteModal?.resourceName}
      />
      
      {/* Pre-Reservation Modal */}
      <PreReservationModal
        isOpen={!!showPreReserveModal}
        onClose={() => setShowPreReserveModal(null)}
        onConfirm={handleConfirmPreReserve}
        resourceName={showPreReserveModal?.resourceName}
        projectTitle={showPreReserveModal?.projectTitle}
        isRMInitiated={showPreReserveModal?.isRMInitiated}
      />

      {/* Bulk Contact Bar for PMs */}
      {isPM && (
        <BulkContactBar
          selectedItems={selectedResources}
          onClearSelection={clearSelection}
          itemType="resources"
        />
      )}

      {/* Bulk Apply Bar for RMs */}
      {isRM && selectedProjects.length > 0 && resource && (
        <BulkApplyBar
          selectedProjects={selectedProjects}
          resource={resource}
          onClearSelection={clearSelection}
        />
      )}
    </div>
  );
};

export default AdvancedMatchingPage;
